import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../LoaderSpinner";

const FILTER_PANEL_MAX_WIDTH_PX = 384;
const FILTER_PANEL_GAP_PX = 8;

function normalizeOptions(options) {
    return (options || [])
        .filter((o) => !(typeof o === "object" && o && o.isHeader))
        .map((o) =>
            typeof o === "string"
                ? { value: o, label: o }
                : { value: String(o.value), label: o.label ?? String(o.value) }
        );
}

export function getCatalogFilterTriggerText(options, selectedOptions, { allText, noneText }) {
    const values = normalizeOptions(options).map((o) => o.value);
    const sel = selectedOptions || [];

    if (sel.length === 0) return allText;
    // if (
    //     values.length > 0 &&
    //     sel.length === values.length &&
    //     values.every((v) => sel.includes(v))
    // ) {
    //     return allText;
    // }
    const normalized = normalizeOptions(options);
    const want = String(selectedOptions[0]);
    const singleLabel = normalized.find((o) => o.value === want)?.label;
    return singleLabel || "";
}

export function ProductCatalogSelectFilter({
    selectClassname = "",
    buttonSelectedClassname = "",
    allLabel,
    noneLabel,
    options = [],
    IconOfSelect = null,
    isLoading = false,
    selectedOptions = [],
    onChange,
    renderOption,
    disabled = false,
    testId,
    /** When false, only the option list is shown (for single-value fields like product sidebar). */
    showAllNone = true,
    /** Fires when the panel opens/closes (e.g. parent row z-index in scrollable drawers). */
    onOpenChange,
    /** Render dropdown in document.body to avoid clipping in nested popovers/drawers. */
    renderInPortal = false,
    /** Keep panel open after choosing an option (useful when parent has a separate Apply button). */
    closeOnSelect = true,
    /** Toolbar filter pill styling: near-black pill when a value is selected (catalog toolbar). */
    activePillStyling = false,
    /** Compact sizing for the catalog toolbar; other usages retain the default width. */
    compact = false,
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const panelRef = useRef(null);
    const onOpenChangeRef = useRef(onOpenChange);
    const [portalStyle, setPortalStyle] = useState(null);
    onOpenChangeRef.current = onOpenChange;

    const flatOptions = useMemo(() => normalizeOptions(options), [options]);
    const allValues = useMemo(() => flatOptions.map((o) => o.value), [flatOptions]);

    // const isAll =
    //     allValues.length > 0 &&
    //     selectedOptions.length === allValues.length &&
    //     allValues.every((v) => selectedOptions.includes(v));
    const isAll = selectedOptions.length === 0;
    const isNone = selectedOptions.length === 0;

    const triggerText = getCatalogFilterTriggerText(options, selectedOptions, {
        allText: allLabel,
        noneText: noneLabel,
    });

    useEffect(() => {
        onOpenChangeRef.current?.(open);
    }, [open]);

    useLayoutEffect(() => {
        if (!open) return;
        const updatePortalPosition = () => {
            if (!renderInPortal || !rootRef.current) return;
            const rect = rootRef.current.getBoundingClientRect();
            const viewportMaxWidth = Math.min(
                FILTER_PANEL_MAX_WIDTH_PX,
                window.innerWidth - FILTER_PANEL_GAP_PX * 2
            );
            const maxWidth = Math.max(
                rect.width,
                viewportMaxWidth
            );
            const panelWidth = Math.max(
                panelRef.current?.getBoundingClientRect().width || 0,
                rect.width
            );
            const left = Math.max(
                FILTER_PANEL_GAP_PX,
                Math.min(rect.left, window.innerWidth - panelWidth - FILTER_PANEL_GAP_PX)
            );
            setPortalStyle({
                position: "fixed",
                left,
                top: rect.bottom + 8,
                minWidth: rect.width,
                width: "max-content",
                maxWidth,
            });
        };
        updatePortalPosition();
        const onDoc = (e) => {
            if (
                rootRef.current &&
                !rootRef.current.contains(e.target) &&
                !(panelRef.current && panelRef.current.contains(e.target))
            ) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        const onViewportChange = () => updatePortalPosition();
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        window.addEventListener("resize", onViewportChange);
        window.addEventListener("scroll", onViewportChange, true);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("resize", onViewportChange);
            window.removeEventListener("scroll", onViewportChange, true);
        };
    }, [flatOptions, open, renderInPortal]);

    const selectAll = () => {
        // onChange([...allValues]);
        onChange([]);
    };

    const selectNone = () => {
        onChange([]);
    };

    const toggleValue = (value) => {
        const v = String(value);
        let next = [];
        if (noneLabel) {
            next = selectedOptions.includes(v) ? [] : [v];
        } else {
            next = [v];
        }
        onChange(next);
        if (closeOnSelect) {
            setOpen(false);
        }
    };

    const panel = open ? (
        <div
            ref={panelRef}
            className={`${renderInPortal ? "" : "absolute left-0 top-full mt-2 min-w-full max-w-[24rem] "}z-[100] max-h-[400px] overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-lg ${selectClassname}`}
            style={
                renderInPortal
                    ? portalStyle || {
                          position: "fixed",
                          left: -9999,
                          top: -9999,
                          visibility: "hidden",
                          width: "max-content",
                          maxWidth: "min(24rem, calc(100vw - 1rem))",
                      }
                    : { width: "max-content", maxWidth: "min(24rem, calc(100vw - 1rem))" }
            }
            role="listbox"
            data-select-portal-panel={renderInPortal ? "true" : undefined}
            data-testid={testId ? `${testId}_panel` : undefined}
        >
            {showAllNone ? (
                <>
                    <button
                        type="button"
                        role="option"
                        aria-selected={isAll}
                        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[13px] text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                        onClick={() => {
                            selectAll();
                            setOpen(false);
                        }}
                    >
                        <span className="truncate">{allLabel}</span>
                        {isAll ? <CheckIcon className="h-4 w-4 shrink-0 text-gray-600" aria-hidden /> : <span className="h-4 w-4 shrink-0" />}
                    </button>

                    {noneLabel && (
                        <button
                            type="button"
                            role="option"
                            aria-selected={isNone}
                            className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[13px] text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                            onClick={() => {
                                selectNone();
                                setOpen(false);
                            }}
                        >
                            <span className="truncate">{noneLabel}</span>
                            {isNone ? <CheckIcon className="h-4 w-4 shrink-0 text-gray-600" aria-hidden /> : <span className="h-4 w-4 shrink-0" />}
                        </button>
                    )}

                    <div className="my-1 border-t border-gray-200" />
                </>
            ) : null}

            {flatOptions.map((opt) => {
                const selected = selectedOptions.includes(opt.value);
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[13px] text-gray-900 hover:bg-gray-50 rounded-md transition-colors ${selected && selectedOptions.length !== flatOptions.length ? "bg-gray-100" : ""}`}
                        onClick={() => toggleValue(opt.value)}
                    >
                        <span className="min-w-0 flex-1 truncate" title={opt.label}>
                            {renderOption ? renderOption(opt) : opt.label}
                        </span>
                        {selected && selectedOptions.length !== flatOptions.length ? (
                            <CheckIcon className="h-4 w-4 shrink-0 text-gray-600" aria-hidden />
                        ) : (
                            <span className="h-4 w-4 shrink-0" aria-hidden />
                        )}
                    </button>
                );
            })}
        </div>
    ) : null;

    return (
        <div
            ref={rootRef}
            className={`relative flex shrink-0 flex-col gap-1 ${
                compact ? "min-w-0 w-auto max-w-[12rem]" : "min-w-[10.5rem]"
            } ${selectClassname}`}
        >
            <button
                type="button"
                disabled={disabled || isLoading}
                data-testid={testId ? `${testId}_trigger` : undefined}
                aria-expanded={open}
                aria-haspopup="listbox"
                className={`transition-colors duration-200 flex h-8 w-full items-center justify-between gap-1.5 rounded-md border px-2.5 text-left text-[13px] font-medium focus:outline-none focus:ring-1 focus:ring-gray-300 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                    activePillStyling && selectedOptions.length > 0
                        ? "border-gray-900 bg-gray-900 text-white hover:bg-gray-800"
                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                } ${isLoading ? "relative" : ""} ${buttonSelectedClassname || ""}`}
                onClick={() => !disabled && setOpen((o) => !o)}
            >
                {isLoading && (
                    <div className="absolute left-0 top-0 w-full h-full flex items-center justify-center">
                        <LoaderSpinner width="20" height="20" colorSpinner="black" border="2" />
                    </div>
                )}
                {IconOfSelect && (
                    <div className="flex items-center justify-center">
                        {IconOfSelect}
                    </div>
                )}
                {isLoading ? (
                    <div className="min-w-0 flex-1 truncate">...</div>
                ) : (
                    <span className="min-w-0 flex-1 truncate text-left leading-tight">{triggerText || "..."}</span>
                )}
                <ChevronDownIcon
                    className={`h-3.5 w-3.5 shrink-0 transition-transform ${
                        activePillStyling && selectedOptions.length > 0 ? "text-white/80" : "text-gray-400"
                    } ${open ? "rotate-180" : ""}`}
                    aria-hidden
                />
            </button>

            {!renderInPortal ? panel : null}
            {renderInPortal && open ? createPortal(panel, document.body) : null}
        </div>
    );
}

export default ProductCatalogSelectFilter;
